import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@/shared/api/rest/client'
import { ENDPOINTS } from '@/shared/api/rest/config'
import { BranchesService } from '@/shared/api/rest/services/branches.service'
import { BranchRecord, ParentRecord } from '@/shared/api/rest/types'
import { RegistryParent } from '../types'

const branchNameById = (branches: BranchRecord[], branchId?: string | null) =>
    branchId ? branches.find((b) => b._id === branchId)?.name ?? '' : ''

type ParentListRow = ParentRecord & { email?: string | null }

/** Server pagination metadata returned by the parents list endpoint. */
export type ParentsMeta = {
    currentPage: number
    totalCount: number
    totalPages: number
    isFirstPage: boolean
    isLastPage: boolean
    hasNextPage: boolean
    hasPreviousPage: boolean
}

type UseParentsParams = {
    page: number
    limit: number
    search?: string
}

/**
 * Fetch a page of the parent registry (`GET /parents?page=&limit=&name=`) with
 * server-side pagination + search, attaching a resolved branch name to each
 * row (the list endpoint returns only `branchId`). A search term containing
 * "@" is sent as an `email` filter, otherwise as `name`.
 */
export const useParents = ({ page, limit, search }: UseParentsParams) => {
    const trimmed = search?.trim()
    const searchParam = trimmed
        ? trimmed.includes('@')
            ? { email: trimmed }
            : { name: trimmed }
        : {}

    const query = useQuery({
        queryKey: ['registry-parents', page, limit, trimmed ?? ''],
        queryFn: async () => {
            const [res, branches] = await Promise.all([
                api.get<{ data: ParentListRow[]; meta?: ParentsMeta }>(
                    ENDPOINTS.PARENTS.LIST({ page, limit, ...searchParam })
                ),
                BranchesService.list().catch(() => [] as BranchRecord[])
            ])
            const parents = (res.data ?? []).map<RegistryParent>((parent) => ({
                ...parent,
                branch: { name: branchNameById(branches, parent.branchId) }
            }))
            return { parents, meta: res.meta ?? null }
        },
        placeholderData: keepPreviousData
    })

    return {
        parents: query.data?.parents ?? [],
        meta: query.data?.meta ?? null,
        loading: query.isLoading,
        fetching: query.isFetching,
        error: query.error ?? null
    }
}
